import type { RemovableRef } from '@vueuse/core';
import type { MaybeRefOrGetter, Ref, WatchHandle } from 'vue';
import type { GlobalCollectionId, JsonObject, MonospaceKeyStorage, Uuid } from '#layers/data-model/public';
import { debouncedWatch, until, useSessionStorage } from '@vueuse/core';
import { klona } from 'klona';
import { computed, ref, toValue, watch } from 'vue';
import { isDeepEqual } from '#layers/base/utils/is-deep-equal';
import { mapValues, pick } from '#layers/base/utils/object';
import { storageKey } from '#layers/base/utils/storage-key';

type KeyStorageRecord = Pick<MonospaceKeyStorage, 'key' | 'value'>;

/** Token built by `first()` and returned as-is from the `query` callback; the tag selects the single-item result. */
interface KeyStorageQuery { '~type': 'first' }

/**
 * The slice of the workspace entity store this composable touches. Structural so `base` stays free of the
 * runtime layer; the active `WorkspaceEntityStore` satisfies it.
 */
export interface PersistedStateStore {
	MonospaceKeyStorage: {
		query: (build: (query: { first: (options: { key: string } | { enabled: false }) => KeyStorageQuery }) => KeyStorageQuery) => {
			data: Ref<KeyStorageRecord | null | undefined>;
			loading: Ref<boolean>;
		};
		create: (item: Partial<MonospaceKeyStorage>) => Promise<unknown>;
		update: (item: Partial<MonospaceKeyStorage>, options: { key: string }) => Promise<unknown>;
		delete: (key: string) => Promise<unknown>;
	};
}

export interface UsePersistedStateOptions {
	/** Entity store of the workspace the state belongs to, usually the active runtime handle's `store`. */
	store: PersistedStateStore;
	/** Slug of that workspace; namespaces the `sessionStorage` fallback. */
	slug: string;
	/** Whether server key storage is available; otherwise use session storage. */
	canUseKeyStorage: MaybeRefOrGetter<boolean>;
	/**
	 * The associated `collectionId`. If set, this state will be associated with the collection and automatically
	 * cleaned up if the collection gets deleted.
	 */
	collectionId?: MaybeRefOrGetter<GlobalCollectionId | null>;
	/**
	 * The associated (root) `blockId`. If set, this state will be associated with the root block and automatically
	 * cleaned up if the block gets deleted.
	 */
	blockId?: MaybeRefOrGetter<Uuid | null>;
	/**
	 * The associated user ID. If set, this state will be associated with the user and automatically
	 * cleaned up if the user gets deleted. Additionally, it is ensured that the state is protected by system permissions.
	 */
	ownerId?: MaybeRefOrGetter<Uuid | null>;

	/**
	 * Toggle the fetching and updating behavior of the persisted state
	 *
	 * @default true
	 */
	enabled?: MaybeRefOrGetter<boolean>;

	/**
	 * Write the default value to the storage when it does not exist
	 *
	 * @default true
	 */
	writeDefaults?: boolean;

	/**
	 * Optional callback function that is invoked when an error occurs interacting with the API.
	 *
	 * @param error The error object or value that was caught or triggered.
	 */
	onError?: (error: unknown) => void;
}

/**
 * Creates a stateful value that is persisted to `MonospaceKeyStorage` using a key.
 * The state reactively updates when the persisted value changes.
 *
 * When the caller disables server key storage the state degrades to `sessionStorage`
 * so local preferences survive without hitting the API.
 *
 * The storage *backend* (plain `ref` vs `sessionStorage`) is bound once at setup: Vue cannot
 * retarget an already-bound ref, so a live availability change does not move existing state between
 * sinks — a component remount is the refresh mechanism. Workspace navigation remounts callers. The reactive bits that *can* change
 * in place — the write gate (`keyStorageEnabled`), the session write gate (`persistToSession`),
 * storage availability, and the session key — are all kept reactive below.
 *
 * @param {MaybeRefOrGetter<string>} key - The unique identifier for the state to persist.
 * @param {MaybeRefOrGetter<T>} defaults - The default value for the state if a persisted value does not already exist or is loading.
 * @param {UsePersistedStateOptions} options - Additional configuration options.
 *
 * @return {RemovableRef<T>} - A reactive reference to the persisted state that can be updated or reset as needed.
 */
export function usePersistedState<T>(
	key: MaybeRefOrGetter<string>,
	defaults: MaybeRefOrGetter<T>,
	options: UsePersistedStateOptions,
): RemovableRef<T> {
	const {
		enabled = true,
		writeDefaults = true,
		onError,
	} = options;

	const { store, slug: workspaceSlug } = options;
	const resolvedKey = computed(() => toValue(key));

	const canUseKeyStorage = computed(() => toValue(options.canUseKeyStorage));

	// The slug is captured with its store: a workspace switch remounts the caller, so both are invariant
	// for this composable instance and the session namespace cannot drift from the cache it mirrors.
	const sessionStorageKey = computed(() => storageKey(`ps:${workspaceSlug}:${resolvedKey.value}`));

	const keyStorageEnabled = computed(() => canUseKeyStorage.value && toValue(enabled));
	// Mirror the keyStorage write gate for the degrade path: only touch session storage while
	// server storage is unavailable and persistence is enabled.
	const persistToSession = computed(() => !canUseKeyStorage.value && toValue(enabled));

	const { data: serverState, loading } = store.MonospaceKeyStorage.query((q) => q.first(keyStorageEnabled.value
		? {
				key: resolvedKey.value,
			}
		: { enabled: false }));

	// Entitled (or session-disabled): keep the plain-ref flow. Degraded + enabled: back the state
	// with sessionStorage, driven by the computed key so vueuse re-binds on key changes, and thread
	// `onError` so private-mode/quota failures surface instead of being swallowed by the default.
	const state = (persistToSession.value
		? useSessionStorage(sessionStorageKey, toValue(defaults), { onError })
		: ref(toValue(defaults))) as RemovableRef<T>;

	/**
	 * Keep track of ongoing writes to prevent sending racing requests.
	 */
	let writing = false;
	/**
	 * If a write is requested while another is ongoing, we store the value here to be written once the ongoing one is finished
	 * so we always end up with the latest value being sent.
	 */
	let writingQueued: { value: T | null } | null = null;

	const { pause: pauseWatch, resume: resumeWatch } = debouncedWatch([state, keyStorageEnabled], ([newState]) => {
		if (keyStorageEnabled.value) {
			write(klona(newState));
		}
	}, { deep: true, debounce: 500 }) as WatchHandle;

	watch([serverState, keyStorageEnabled], ([result]) => {
		if (
			!keyStorageEnabled.value
			|| !result
			|| result.key !== resolvedKey.value
			|| isDeepEqual(result.value, state.value)
			|| writing
		) {
			return;
		}

		pauseWatch();
		state.value = klona(result.value) as T;
		resumeWatch();
	}, { immediate: true, deep: true });

	(async () => {
		await until(loading).toBe(false);

		if (!serverState.value && writeDefaults && keyStorageEnabled.value) {
			write(state.value);
		}
	})();

	return state;

	async function create(initialValue: T) {
		try {
			const additionalOptions = mapValues(pick(options, ['collectionId', 'blockId', 'ownerId']), toValue);
			const nonNullOptions = Object.fromEntries(Object.entries(additionalOptions).filter(([, value]) => value !== null));

			await store.MonospaceKeyStorage.create({
				key: resolvedKey.value,
				value: initialValue as JsonObject,
				...nonNullOptions,
			});
		}
		catch (error) {
			onError?.(error);
		}
	}

	async function write(value: T | null) {
		if (writing) {
			writingQueued = { value };
			return;
		}

		writing = true;

		try {
			if (value !== null && !serverState.value) {
				await create(value);
			}
			else if (value !== null && !isDeepEqual(value, serverState.value!.value)) {
				await store.MonospaceKeyStorage.update({
					value,
				}, {
					key: resolvedKey.value,
				});
			}
			else if (value === null) {
				await store.MonospaceKeyStorage.delete(resolvedKey.value);
			}
		}
		catch (error) {
			onError?.(error);
		}

		writing = false;

		if (writingQueued !== null) {
			const queuedValue = writingQueued.value;
			writingQueued = null;
			return write(queuedValue);
		}
	}
}
